'use client';

import { useMemo } from 'react';
import { useTranslations } from 'next-intl';

import { SAVED_SEARCH_FREQUENCY_OPTIONS } from '@/src/components/ListingSearch/saveSearchFormUtils';
import { useFilters } from '@/src/lib/filters';
// Through the barrel, like SrpSaveSearchProvider beside it. Deep-importing useSavedSearches
// pulls the real API client past that provider's test mock, into a file that renders outside
// jsdom — `window is not defined` before a single assertion runs.
import { useCreateSavedSearch, useCurrentUser, useSaveSearchTarget } from '@/src/lib/hooks/query';
import { buildSavedSearchPayload, type SavedSearchFrequency } from '@/src/lib/savedSearches';
import type { SaveSearchDefaults } from '@/src/components/ListingSearch/SaveSearchModal';
import { identityLoadingResult, textResult, useWebMcpTools } from '@/src/lib/webmcp';
import type { WebMcpTool } from '@/src/lib/webmcp';

type WebMcpSaveSearchToolProps = {
  isSaved: boolean;
  /**
   * The saved-search list is still in flight, so `isSaved` is not yet trustworthy. The Save
   * button is disabled for exactly this window; without it the tool would read "not saved" for an
   * alert the renter already has and post a duplicate.
   */
  isSavedStateResolving: boolean;
  /**
   * The dialog is waiting on the renter's sign-in code with the search they already submitted. A
   * new proposal now would change the form behind the step but not what gets saved.
   */
  isSigningIn: boolean;
  /** Opens the save-search dialog, pre-filled with the supplied name and cadence. */
  openDialog: (defaults: SaveSearchDefaults) => void;
  onSaved: () => void;
};

/**
 * `save_search` — turns the SRP's current filters into a search alert.
 *
 * Runs directly for a signed-in renter and falls back to the dialog for a guest, because an alert
 * needs a signed-in account — a real email plus the code sent to it — and an agent must never
 * invent either. Reasoning in
 * docs/HP-7461/webmcp-actions.md.
 *
 * Rendered by SrpSaveSearchProvider only while a route has seeded a save-search target, and reads
 * the payload fields from that same `useSaveSearchTarget` seam.
 */
export function WebMcpSaveSearchTool({
  isSaved,
  isSavedStateResolving,
  isSigningIn,
  openDialog,
  onSaved,
}: WebMcpSaveSearchToolProps) {
  const t = useTranslations('saveSearchModal');
  const { filters } = useFilters();
  const { data: target } = useSaveSearchTarget();
  const { data: currentUser } = useCurrentUser();
  const createSavedSearch = useCreateSavedSearch();
  // The dialog's real submit label, not a hardcoded "Save" — it is translated on the /es routes.
  const submitLabel = t('submit');

  const tools = useMemo<Array<WebMcpTool>>(
    () => [
      {
        name: 'save_search',
        description:
          'Save the filters currently applied to this results page as a search alert, so the renter gets an email when a new rental matches them. Saving needs a signed-in account: if nobody is signed in, this opens the save-search dialog instead of saving, where the renter enters their email, presses Save, and then enters the sign-in code emailed to them.',
        inputSchema: {
          type: 'object',
          properties: {
            name: {
              type: 'string',
              description: 'A short name for this alert, such as "Two-bed in Capitol Hill". Defaults to the area name.',
            },
            frequency: {
              type: 'string',
              enum: [...SAVED_SEARCH_FREQUENCY_OPTIONS],
              description:
                'How often to email the renter about new matches. "never" saves the search without sending any email. Defaults to "instant".',
            },
          },
          required: [],
        },
        execute: async (args) => {
          const { name, frequency } = args;
          if (name !== undefined && typeof name !== 'string') {
            return textResult('The name must be text.');
          }
          if (frequency !== undefined && !SAVED_SEARCH_FREQUENCY_OPTIONS.includes(frequency as SavedSearchFrequency)) {
            return textResult(`frequency must be one of: ${SAVED_SEARCH_FREQUENCY_OPTIONS.join(', ')}.`);
          }

          if (!target) return textResult('There is no search on this page to save.');
          if (isSaved) return textResult('This search is already saved. Its alerts are already running.');
          if (isSigningIn) {
            return textResult(
              'The renter is confirming their email with a sign-in code, and the search they submitted is saved once they do. Call this tool again after that if they want a different alert.',
            );
          }
          if (currentUser === undefined) return identityLoadingResult();
          if (isSavedStateResolving) {
            return textResult(
              "The renter's existing search alerts are still loading, so this would risk saving a duplicate. Call this tool again in a moment.",
            );
          }

          // The page's H1 is the default, but a route can resolve to none. Say so rather than
          // posting a nameless alert the backend rejects — "call this tool again" would loop the agent.
          const searchName = name?.trim() || target.defaultName.trim();
          if (!searchName) {
            return textResult('This alert needs a name. Pass one in `name`, such as "Two-bed in Capitol Hill".');
          }

          const searchFrequency = (frequency as SavedSearchFrequency | undefined) ?? 'instant';

          if (!currentUser?.loggedIn) {
            openDialog({ name: searchName, frequency: searchFrequency });
            return textResult(
              `This search is not saved yet, because an alert needs a signed-in account to send to. The save-search dialog is now open, named "${searchName}". Tell the renter to enter their email, press "${submitLabel}", then enter the sign-in code emailed to them.`,
            );
          }

          const email = (currentUser.data?.email ?? '').trim();
          if (!email) {
            // The dialog asks for an email only when nobody is signed in, so opening it for a
            // signed-in account with none on file would hide that field and leave Save disabled.
            return textResult(
              'This search cannot be saved: the signed-in HotPads account has no email address on file, and an alert needs somewhere to send. The renter has to add one to their account first.',
            );
          }

          try {
            await createSavedSearch.mutateAsync({
              postData: buildSavedSearchPayload({
                name: searchName,
                frequency: searchFrequency,
                areaId: target.areaId,
                filters,
                areaBounds: target.areaBounds,
                searchScope: target.searchScope,
              }),
            });
          } catch {
            return textResult(`Could not save this search. Call this tool again in a moment.`);
          }

          onSaved();

          return searchFrequency === 'never'
            ? textResult(`Saved this search as "${searchName}". It sends no alert email.`)
            : textResult(`Saved this search as "${searchName}". New matches go to ${email} ${searchFrequency}.`);
        },
      },
    ],
    [
      createSavedSearch,
      currentUser,
      filters,
      isSaved,
      isSavedStateResolving,
      isSigningIn,
      onSaved,
      openDialog,
      submitLabel,
      target,
    ],
  );

  useWebMcpTools(tools);

  return null;
}
